import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ApiEnvironment = "development" | "test";

export interface OrgApiEnvironmentRow {
  id: string;
  organization_id: string;
  environment: ApiEnvironment;
  org_id_value: string | null;
  api_token: string | null;
  api_endpoint_url: string | null;
  api_endpoint_token: string | null;
  allocated_devices: string | null;
  updated_at: string;
  updated_by: string | null;
}

export interface OrgApiEnvironmentInput {
  org_id_value?: string | null;
  api_token?: string | null;
  api_endpoint_url?: string | null;
  api_endpoint_token?: string | null;
  allocated_devices?: string | null;
}

export const useOrgApiEnvironment = (
  organizationId: string,
  environment: ApiEnvironment
) => {
  return useQuery({
    queryKey: ["org-api-environment", organizationId, environment],
    queryFn: async (): Promise<OrgApiEnvironmentRow | null> => {
      if (!organizationId) return null;
      const { data, error } = await (supabase as any)
        .from("organization_api_environments")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("environment", environment)
        .maybeSingle();
      if (error) throw error;
      return (data as OrgApiEnvironmentRow | null) ?? null;
    },
    enabled: !!organizationId,
  });
};

export const useUpsertOrgApiEnvironment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      organizationId,
      environment,
      values,
    }: {
      organizationId: string;
      environment: ApiEnvironment;
      values: OrgApiEnvironmentInput;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const payload = {
        organization_id: organizationId,
        environment,
        org_id_value: values.org_id_value ?? null,
        api_token: values.api_token ?? null,
        api_endpoint_url: values.api_endpoint_url ?? null,
        api_endpoint_token: values.api_endpoint_token ?? null,
        allocated_devices: values.allocated_devices ?? null,
        updated_by: user?.id ?? null,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await (supabase as any)
        .from("organization_api_environments")
        .upsert(payload, { onConflict: "organization_id,environment" })
        .select()
        .maybeSingle();
      if (error) throw error;
      return data as OrgApiEnvironmentRow;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["org-api-environment", vars.organizationId, vars.environment],
      });
    },
  });
};